import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Loader2, 
  CheckCircle, 
  CreditCard, 
  Building, 
  FileText,
  AlertCircle,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import WowCXLogo from '@/components/WowCXLogo';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const CustomerPaymentPage = () => {
  const { paymentToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    fetchPaymentDetails();
  }, [paymentToken]);

  const fetchPaymentDetails = async () => {
    try {
      const response = await axios.get(`${API}/projects/pay/${paymentToken}`);
      setPaymentData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Payment link not found or has expired.');
      } else if (err.response?.status === 410) {
        setError('This payment link has expired. Please contact support for a new link.');
      } else if (err.response?.status === 400) {
        setError('This payment has already been completed.');
        setPaymentComplete(true);
      } else {
        setError('Failed to load payment details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    
    try {
      // Create Razorpay order
      const orderResponse = await axios.post(`${API}/payments/create-payment-link-order`, {
        payment_token: paymentToken
      });

      const { order_id, amount, key_id } = orderResponse.data;

      // Load Razorpay script
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      const options = {
        key: key_id,
        amount: amount,
        currency: paymentData.currency,
        name: 'Wow!CX Design Studio',
        description: `Payment for ${paymentData.business_name || 'Website Design'}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/payments/verify-payment-link`, {
              payment_token: paymentToken,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            setPaymentComplete(true);
            toast.success('Payment successful! Thank you.');
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: paymentData.customer_name || '',
        },
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            {paymentComplete ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-zinc-900 mb-2">Payment Already Completed</h2>
                <p className="text-zinc-600">This payment has already been processed. Thank you!</p>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-zinc-900 mb-2">Payment Link Error</h2>
                <p className="text-zinc-600">{error}</p>
              </>
            )}
            <Button className="mt-6" onClick={() => window.location.href = 'https://wowcx.io'}>
              Visit Wow!CX
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">Payment Successful!</h2>
            <p className="text-zinc-600 mb-6">
              Thank you for your payment. Our team will begin working on your project shortly.
            </p>
            <p className="text-sm text-zinc-500">
              You will receive a confirmation email with next steps.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currencySymbol = paymentData.currency === 'INR' ? '₹' : '$';

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
      {/* Header */}
      <header className="px-6 py-4 bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <WowCXLogo size="md" />
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Shield className="w-4 h-4" />
            Secure Payment
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-2" style={{fontFamily: 'Outfit'}}>
              Complete Your Payment
            </h1>
            {paymentData.business_name && (
              <p className="text-zinc-600 flex items-center justify-center gap-2">
                <Building className="w-4 h-4" />
                {paymentData.business_name}
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {paymentData.items.map((item, i) => (
                    <div key={i} className="flex justify-between items-start py-2 border-b border-zinc-100 last:border-0">
                      <div>
                        <p className="font-medium text-zinc-900">{item.name}</p>
                        {item.description && (
                          <p className="text-xs text-zinc-500">{item.description}</p>
                        )}
                      </div>
                      <p className="font-semibold text-zinc-900">
                        {currencySymbol}{item.amount.toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t-2 border-zinc-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-zinc-900">Total</span>
                    <span className="text-2xl font-bold text-indigo-600">
                      {currencySymbol}{paymentData.total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Action */}
            <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-white/80" />
                  <h3 className="text-xl font-bold mb-2">Ready to Pay?</h3>
                  <p className="text-white/80 text-sm">
                    Click below to complete your payment securely via Razorpay
                  </p>
                </div>

                <Button 
                  className="w-full bg-white text-indigo-600 hover:bg-zinc-100 font-semibold py-6 text-lg"
                  onClick={handlePayment}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Pay {currencySymbol}{paymentData.total.toLocaleString()}
                    </>
                  )}
                </Button>

                <div className="mt-6 space-y-2 text-center text-white/70 text-xs">
                  <p className="flex items-center justify-center gap-1">
                    <Shield className="w-3 h-3" />
                    256-bit SSL Encrypted
                  </p>
                  <p>Powered by Razorpay</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <p className="text-sm text-zinc-500">
              Questions? Contact us at{' '}
              <a href="mailto:support@wowcx.io" className="text-indigo-600 hover:underline">
                support@wowcx.io
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerPaymentPage;
